import React, { useState, useEffect } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    FormGroup,
    Label,
    Badge,
    Spinner,
    Alert,
    Card,
    CardBody,
    Row,
    Col
} from "reactstrap";
import axios from "axios";
import { useAuth } from "../../Provider/AuthProvider";

interface Product {
    id: string;
    ref: string;
    brand_id: string;
    brand_name: string;
    label: string;
    stock: number;
    barcode: string;
    fournisseur?: string;
    achat_prices?: any;
    seuil_stock_alerte?: number;
}

interface StockCorrectionModalProps {
    isOpen: boolean;
    toggle: () => void;
    onCorrectionSuccess?: () => void;
}

const StockCorrectionModal: React.FC<StockCorrectionModalProps> = ({
    isOpen,
    toggle,
    onCorrectionSuccess
}) => {
    const { user } = useAuth();
    const [productSearch, setProductSearch] = useState("");
    const [searching, setSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<Product[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [correctionAction, setCorrectionAction] = useState<"add" | "remove">("add");
    const [quantity, setQuantity] = useState<number>(1);
    const [source, setSource] = useState<"inventory" | "stock_correction">("inventory");
    const [seuilAlerte, setSeuilAlerte] = useState<number | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [seuilMode, setSeuilMode] = useState(false);
    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            resetForm();
        }
    }, [isOpen]);

    const resetForm = () => {
        setProductSearch("");
        setSearchResults([]);
        setSelectedProduct(null);
        setCorrectionAction("add");
        setQuantity(1);
        setSource("inventory");
        setSeuilAlerte(null);
        setMessage(null);
        setSeuilMode(false);
    };

    // Search products API
    const searchProducts = async (term: string) => {
        if (term.length < 3) {
            setSearchResults([]);
            return;
        }

        try {
            setSearching(true);
            setMessage(null);

            const response: any = await axios.post(
                "https://api.apapiecesauto.fr/apiV2/",
                {
                    action: "erpref",
                    term: term,
                    entity: user.entity
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${user.token}`
                    }
                }
            );

            if (response.success) {
                setSearchResults(response.data || []);
            } else {
                throw new Error(response.message || "Failed to search products");
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message || "Erreur lors de la recherche de produits" });
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    };

    // Handle product search with debounce
    const handleProductSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setProductSearch(value);

        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        if (value.length >= 3) {
            const timeout = setTimeout(() => {
                searchProducts(value);
            }, 500);
            setSearchTimeout(timeout);
        } else {
            setSearchResults([]);
        }
    };

    const handleSelectProduct = (product: Product) => {
        setSelectedProduct(product);
        setSeuilAlerte(product.seuil_stock_alerte || 0);
        setProductSearch("");
        setSearchResults([]);
    };

    const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value);
        if (!isNaN(value) && value >= 1) {
            setQuantity(value);
        } else if (e.target.value === "") {
            setQuantity(1);
        }
    };

    const handleSeuilAlerteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value === "") {
            setSeuilAlerte(null);
        } else {
            const numValue = parseInt(value);
            if (!isNaN(numValue) && numValue >= 0) {
                setSeuilAlerte(numValue);
            }
        }
    };

    const handleSubmitCorrection = async () => {
        if (!selectedProduct) {
            setMessage({ type: 'error', text: "Veuillez sélectionner un produit" });
            return;
        }
        debugger
        try {
            setSubmitting(true);
            setMessage(null);

            // Préparer les données selon le mode
            let correctionData;

            if (seuilMode) {
                // MODE SEUIL UNIQUEMENT - Envoyer seulement seuil_alerte
                if (seuilAlerte === null) {
                    setMessage({ type: 'error', text: "Veuillez définir un seuil" });
                    setSubmitting(false);
                    return;
                }

                correctionData = {
                    action: "correctstock",
                    entity: user.entity,
                    product_id: parseInt(selectedProduct.id),
                    seuil_alerte: seuilAlerte
                    // PAS de qty, PAS de source_type - seulement seuil_alerte
                };
            } else {
                // MODE STOCK - Envoyer tout SAUF seuil_alerte
                if (!quantity || quantity < 1) {
                    setMessage({ type: 'error', text: "La quantité doit être au moins 1" });
                    setSubmitting(false);
                    return;
                }

                correctionData = {
                    action: "correctstock",
                    entity: user.entity,
                    product_id: parseInt(selectedProduct.id),
                    qty: correctionAction === "add" ? quantity : quantity * -1,
                    source_type: source === "inventory" ? 1 : 2
                    // PAS de seuil_alerte ici
                };
            }

            // Debug: voir ce qu'on envoie
            console.log("Données envoyées:", correctionData);

            const response: any = await axios.post(
                "https://api.apapiecesauto.fr/apiV2/",
                correctionData,
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${user.token}`
                    }
                }
            );

            if (response.success) {
                setMessage({
                    type: 'success',
                    text: seuilMode
                        ? `Seuil d'alerte mis à jour à ${seuilAlerte} unités!`
                        : `Correction de stock effectuée avec succès!`
                });

                if (onCorrectionSuccess) {
                    onCorrectionSuccess();
                }

                setTimeout(() => {
                    resetForm();
                    setSeuilMode(false); // Réinitialiser le mode
                    toggle();
                }, 2000);
            } else {
                throw new Error(response.message || "Échec de l'opération");
            }
        } catch (err: any) {
            setMessage({
                type: 'error',
                text: err.message || "Erreur lors de l'opération"
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" centered className="stock-correction-modal">
            <ModalHeader toggle={toggle} className="bg-light border-bottom-0 pb-0">
            </ModalHeader>

            <ModalBody className="pt-4">
                {message && (
                    <Alert
                        color={message.type === 'success' ? 'success' : 'danger'}
                        className="border-0 rounded-3 shadow-sm"
                        isOpen={!!message}
                        toggle={() => setMessage(null)}
                    >
                        <div className="d-flex align-items-center">
                            <i className={`ri-${message.type === 'success' ? 'checkbox-circle' : 'error-warning'}-fill fs-5 me-3`}></i>
                            <div className="flex-grow-1">{message.text}</div>
                        </div>
                    </Alert>
                )}

                {/* Search Section - Only show when no product selected */}
                {!selectedProduct && (
                    <div className="search-section mb-4">
                        <FormGroup>
                            <Label for="productSearch" className="fw-semibold mb-3">
                                <i className="ri-search-line me-2"></i>
                                Rechercher un produit
                            </Label>
                            <div className="position-relative">
                                <Input
                                    id="productSearch"
                                    type="text"
                                    placeholder="Saisir référence ou nom du produit..."
                                    value={productSearch}
                                    onChange={handleProductSearchChange}
                                    className="border-2 rounded-pill ps-4 py-2"
                                />
                                <i className="ri-search-line position-absolute top-50 end-0 translate-middle-y me-3 text-muted"></i>
                            </div>

                            {searching && (
                                <div className="mt-2 text-primary">
                                    <Spinner size="sm" className="me-2" />
                                    <small>Recherche en cours...</small>
                                </div>
                            )}
                        </FormGroup>

                        {/* Search Results */}
                        {searchResults.length > 0 && (
                            <Card className="border-0 shadow-sm mt-3">
                                <CardBody className="p-0">
                                    <div className="p-3 bg-light border-bottom">
                                        <small className="text-muted">
                                            <i className="ri-list-check me-2"></i>
                                            {searchResults.length} produit{searchResults.length > 1 ? 's' : ''} trouvé{searchResults.length > 1 ? 's' : ''}
                                        </small>
                                    </div>
                                    <div className="search-results" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                        {searchResults.map((product) => (
                                            <div
                                                key={product.id}
                                                className="p-3 border-bottom hover-bg"
                                                onClick={() => handleSelectProduct(product)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <div className="d-flex align-items-center mb-2">
                                                            <span className="fw-bold text-primary me-3">{product.ref}</span>
                                                            <Badge color="light" className="text-dark border">
                                                                {product.brand_name}
                                                            </Badge>
                                                        </div>
                                                        <p className="text-muted small mb-2">{product.label}</p>
                                                        <div className="d-flex align-items-center">
                                                            <Badge
                                                                color={product.stock > 0 ? "success" : "secondary"}
                                                                className="me-3"
                                                            >
                                                                Stock: {product.stock}
                                                            </Badge>
                                                            {product.seuil_stock_alerte && (
                                                                <Badge color="light" className="text-dark border">
                                                                    Seuil: {product.seuil_stock_alerte}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <Button
                                                        color="primary"
                                                        size="sm"
                                                        className="rounded-pill px-3"
                                                        onClick={() => handleSelectProduct(product)}
                                                    >
                                                        <i className="ri-arrow-right-line me-1"></i>
                                                        Sélectionner
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardBody>
                            </Card>
                        )}
                    </div>
                )}

                {/* Selected Product Section */}
                {selectedProduct && (
                    <>
                        {/* Product Card - Compact Design */}
                        <Card className="border-0 shadow-sm mb-3">
                            <CardBody className="p-3">
                                <div className="d-flex align-items-center mb-3">
                                    <Button
                                        color="outline-danger"
                                        size="sm"
                                        onClick={() => setSelectedProduct(null)}
                                        className="rounded-pill px-3 py-1 ms-auto"
                                    >
                                        <i className="ri-close-line me-1"></i>
                                        Changer
                                    </Button>
                                </div>


                                {/* Compact 3-3-3-3 Grid Layout */}
                                <div className="product-info bg-light rounded-2 p-2">
                                    <Row className="g-2 align-items-center">
                                        {/* Référence */}
                                        <Col md={3}>
                                            <div className="text-center p-1">
                                                <small className="text-muted d-block mb-1">Réf</small>
                                                <span className="fw-bold text-primary d-block text-truncate">
                                                    {selectedProduct.ref}
                                                </span>
                                            </div>
                                        </Col>

                                        {/* Marque */}
                                        <Col md={3}>
                                            <div className="text-center p-1">
                                                <small className="text-muted d-block mb-1">Marque</small>
                                                <Badge
                                                    color="light"
                                                    className="text-dark border fw-normal px-2 py-1 d-inline-block text-truncate"
                                                    style={{ maxWidth: '100%' }}
                                                >
                                                    {selectedProduct.brand_name}
                                                </Badge>
                                            </div>
                                        </Col>

                                        {/* Stock */}
                                        <Col md={3}>
                                            <div className="text-center p-1">
                                                <small className="text-muted d-block mb-1">Stock</small>
                                                <div className="d-flex flex-column align-items-center">
                                                    <Badge
                                                        color={selectedProduct.stock > 0 ? "success" : "danger"}
                                                        className="fw-bold px-3 py-2 mb-1"
                                                    >
                                                        {selectedProduct.stock}
                                                    </Badge>
                                                    {selectedProduct.seuil_stock_alerte && (
                                                        <small className="text-muted">
                                                            Seuil actuel: <span className="fw-bold">{selectedProduct.seuil_stock_alerte}</span>
                                                        </small>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>

                                        {/* Désignation */}
                                        <Col md={3}>
                                            <div className="text-center p-1">
                                                <small className="text-muted d-block mb-1">Désignation</small>
                                                <p className="mb-0 text-truncate small" title={selectedProduct.label}>
                                                    {selectedProduct.label}
                                                </p>
                                             
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </CardBody>
                        </Card>

                        {/* Correction Settings - More Compact */}
                        <Card className="border-0 shadow-sm mb-3">
                            <CardBody className="p-3">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="d-flex align-items-center">
                                        <i className="ri-settings-3-line text-primary me-2"></i>
                                        <h6 className="mb-0">
                                            {seuilMode ? "Paramètre Seuil " : "correction stock"}
                                        </h6>
                                    </div>
                                    <Button
                                        color={seuilMode ? "primary" : "warning"}
                                        size="sm"
                                        onClick={() => setSeuilMode(!seuilMode)}
                                        className="rounded-pill px-3"
                                    >
                                        <i className={seuilMode ? "ri-stock-line me-1" : "ri-line-chart-line me-1"}></i>
                                        {seuilMode ? "Paramètre Stock" : "Paramètre Seuil"}
                                    </Button>
                                </div>

                                {/* Interface conditionnelle */}
                                {seuilMode ? (
                                    // Interface SEUIL uniquement
                                    <div className="seuil-only-section bg-warning bg-opacity-10 rounded-2 p-3">
                                        <FormGroup>
                                            <Label className="fw-bold mb-2">
                                                <i className="ri-alarm-warning-line me-2"></i>
                                                Seuil d'alerte
                                            </Label>
                                            <Input
                                                type="number"
                                                min="0"
                                                value={seuilAlerte === null ? '' : seuilAlerte}
                                                onChange={handleSeuilAlerteChange}
                                                placeholder="Définir le seuil d'alerte..."
                                                className="border-warning"
                                            />
                                            <small className="text-muted mt-1">
                                                Mise à jour uniquement du seuil. Le stock reste inchangé.
                                            </small>
                                        </FormGroup>
                                    </div>
                                ) : (
                                    // Interface NORMALE - SANS seuil_alerte
                                    <div className="correction-controls bg-light rounded-2 p-3">
                                        <Row className="g-3 align-items-end">
                                            <Col md={4}>
                                                <FormGroup className="mb-0">
                                                    <Label size="sm" className="fw-semibold mb-1">
                                                        <i className="ri-add-circle-line me-1"></i>
                                                        Action
                                                    </Label>
                                                    <Input
                                                        type="select"
                                                        value={correctionAction}
                                                        onChange={(e) => setCorrectionAction(e.target.value as "add" | "remove")}
                                                        className="border-2"
                                                    >
                                                        <option value="add">Ajouter</option>
                                                        <option value="remove">Supprimer</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>

                                            <Col md={4}>
                                                <FormGroup className="mb-0">
                                                    <Label size="sm" className="fw-semibold mb-1">
                                                        Quantité
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        value={quantity}
                                                        onChange={handleQuantityChange}
                                                        className="border-2"
                                                    />
                                                </FormGroup>
                                            </Col>

                                            <Col md={4}>
                                                <FormGroup className="mb-0">
                                                    <Label size="sm" className="fw-semibold mb-1">
                                                        <i className="ri-folder-info-line me-1"></i>
                                                        Source
                                                    </Label>
                                                    <Input
                                                        type="select"
                                                        value={source}
                                                        onChange={(e) => setSource(e.target.value as "inventory" | "stock_correction")}
                                                        className="border-2"
                                                    >
                                                        <option value="inventory">Inventaire</option>
                                                        <option value="stock_correction">Correction stock</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </div>
                                )}
                            </CardBody>
                        </Card>

                        {/* Summary - More Compact     <Card className="border-0 shadow-sm">
                            <CardBody className="p-3">
                                <div className="d-flex align-items-center mb-3">
                                    <i className="ri-file-list-3-line text-primary me-2"></i>
                                    <h6 className="mb-0">Récapitulatif</h6>
                                </div>

                                <div className="summary-grid">
                                    <Row className="g-2 mb-2">
                                        <Col md={2}>
                                            <div className="p-2 bg-white border rounded text-center">
                                                <small className="text-muted d-block">Action</small>
                                                <div className={`fw-bold small ${correctionAction === "add" ? "text-success" : "text-danger"}`}>
                                                    {correctionAction === "add" ? "Ajout" : "Retrait"}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={2}>
                                            <div className="p-2 bg-white border rounded text-center">
                                                <small className="text-muted d-block">Quantité</small>
                                                <div className="fw-bold">{quantity}</div>
                                            </div>
                                        </Col>
                                        <Col md={2}>
                                            <div className="p-2 bg-white border rounded text-center">
                                                <small className="text-muted d-block">Stock actuel</small>
                                                <div className="fw-bold">{selectedProduct.stock}</div>
                                            </div>
                                        </Col>
                                        <Col md={2}>
                                            <div className="p-2 bg-white border rounded text-center">
                                                <small className="text-muted d-block">Seuil alerte</small>
                                                <div className={`fw-bold ${seuilAlerte !== null ? 'text-warning' : 'text-muted'}`}>
                                                    {seuilAlerte !== null ? seuilAlerte : "Non défini"}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={2}>
                                            <div className="p-2 bg-white border rounded text-center">
                                                <small className="text-muted d-block">Nouveau stock</small>
                                                <div className="fw-bold text-primary">
                                                    {selectedProduct.stock + (correctionAction === "add" ? quantity : -quantity)}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={2}>
                                            <div className="p-2 bg-white border rounded text-center">
                                                <div className="fw-bold">
                                                    {seuilAlerte !== null &&
                                                        selectedProduct.stock + (correctionAction === "add" ? quantity : -quantity) <= seuilAlerte ? (
                                                        <Badge color="warning" className="px-2">
                                                            <i className="ri-alarm-warning-line me-1"></i>
                                                            Alerte
                                                        </Badge>
                                                    ) : (
                                                        <Badge color="success" className="px-2">
                                                            OK
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>


                                    <div className="text-center mt-3">
                                        <Badge
                                            color={correctionAction === "add" ? "success" : "danger"}
                                            className="px-3 py-1"
                                        >
                                            {correctionAction === "add" ? '+' : '-'}{quantity} unité{quantity > 1 ? 's' : ''}
                                        </Badge>
                                        {seuilAlerte !== null && (
                                            <Badge color="light" className="text-dark border ms-4 px-4 py-2">
                                                <i className="ri-alarm-warning-line me-1"></i>
                                                Seuil: {seuilAlerte}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </CardBody>
                        </Card> */}
                     
                    </>
                )}
            </ModalBody>

            <ModalFooter className="border-top-0 bg-light">
                <Button
                    color="light"
                    onClick={toggle}
                    disabled={submitting}
                    className="rounded-pill px-4"
                >
                    <i className="ri-close-line me-2"></i>
                    Annuler
                </Button>
                <Button
                    color="primary"
                    onClick={handleSubmitCorrection}
                    disabled={!selectedProduct || submitting}
                    className="rounded-pill px-4 shadow-sm"
                >
                    {submitting ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Traitement...
                        </>
                    ) : (
                        <>
                            <i className="ri-check-double-line me-2"></i>
                            Confirmer la correction
                        </>
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default StockCorrectionModal;